import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useScannerAuth } from '@/hooks/attendance/useScannerAuth';
import { toast } from '@/hooks/ui/use-toast';
import { Camera, Loader2, AlertCircle, CheckCircle2, Keyboard, ShieldCheck, LogIn } from 'lucide-react';
import QRCodeScanner from '@/components/QRCodeScanner';
import PassUpdateForm from '@/components/PassUpdateForm';
import ScannerLoginForm from '@/components/auth/ScannerLoginForm';
import { supabase } from '@/integrations/supabase/client';

interface QRCodeData {
  customer_id: string;
  loyalty_program_id: string;
  business_id: string;
  pass_id: string;
}

interface CustomerLoyaltyInfo {
  customer_id: string;
  customer_name?: string;
  program_name: string;
  program_type: 'points' | 'stamps';
  current_points: number;
  current_stamps: number;
  total_visits: number;
  business_id: string;
  loyalty_program_id?: string;
  wallet_type?: 'apple' | 'google';
}

interface CouponInfo {
  type: 'coupon';
  coupon_offer_id: string;
  customer_id: string;
  coupon_name: string;
  coupon_header: string;
  coupon_description: string;
  discount_type: string;
  discount_value: number;
  minimum_purchase: number;
  business_id: string;
  business_name: string;
  pass_id: string;
  pass_uuid?: string; // Actual pass UUID for APNs calls
  google_wallet_object_id?: string;
  platform: 'apple_wallet' | 'google_wallet';
  valid_until?: string;
  can_redeem: boolean;
  already_redeemed: boolean;
  redemption_date?: string;
}

type PassInfo = CustomerLoyaltyInfo | CouponInfo;

const UpdatePassPage: React.FC = () => {
  const { session: scannerSession, isAuthenticated, loading: scannerLoading, logout: scannerLogout, programId, businessId, programName, username } = useScannerAuth();
  const [scanning, setScanning] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<PassInfo | null>(null);
  const [originalCustomerInfo, setOriginalCustomerInfo] = useState<PassInfo | null>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualQRData, setManualQRData] = useState('');
  const [lastProcessedQR, setLastProcessedQR] = useState<string | null>(null);
  const [currentPassId, setCurrentPassId] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<{action: string, value?: number, timestamp: number} | null>(null);
  const [undoCountdown, setUndoCountdown] = useState<number | null>(null);
  const [visitCounted, setVisitCounted] = useState(false);
  const [pendingPoints, setPendingPoints] = useState(0);
  const [pendingStamps, setPendingStamps] = useState(0);
  const undoIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoStartedRef = useRef<boolean>(false);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (undoIntervalRef.current) {
        clearInterval(undoIntervalRef.current);
      }
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array means this runs once on mount

  const startScanning = useCallback(async () => {
    console.log('📷 START SCANNING CALLED - Who called this?', new Error().stack);
    
    // Check if we're on HTTPS or localhost
    const isSecureContext = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    
    if (!isSecureContext) {
      setError("Camera access requires HTTPS. Please use manual entry or switch to HTTPS.");
      return;
    }

    setScanning(true);
    setError(null);
    setSuccess(null);
    setManualEntry(false);
    setLastProcessedQR(null); // Reset last processed QR when starting new scan
    
    try {
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera not supported in this browser");
      }

      toast.info("QR Scanner Starting - Please allow camera access when prompted");
      
    } catch (err) {
      setError("Failed to start camera. Please use manual entry or ensure camera permissions are granted.");
      setScanning(false);
    }
  }, []); // Empty dependency array since this function doesn't depend on any state/props

  // Auto-start scanning when component mounts and user is authenticated  
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // Only auto-start once when authenticated becomes true and we haven't auto-started before
    if (isAuthenticated && !hasAutoStartedRef.current) {
      hasAutoStartedRef.current = true;
      // Wait a bit for component to fully mount
      timeoutId = setTimeout(() => {
        startScanning();
      }, 500);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAuthenticated, startScanning]); // Include startScanning but use ref to prevent multiple runs

  const stopScanning = () => {
    setScanning(false);
    setCustomerInfo(null);
    setOriginalCustomerInfo(null);
    // Reset auto-start flag so manual start can work
    hasAutoStartedRef.current = false;
  };

  const startManualEntry = () => {
    setManualEntry(true);
    setScanning(false);
    setError(null);
    setSuccess(null);
    setLastProcessedQR(null); // Reset last processed QR when switching to manual entry
  };

  const handleManualSubmit = async () => {
    if (!manualQRData.trim()) {
      setError("Please enter QR code data");
      return;
    }

    try {
      await handleQRCodeDetected(manualQRData);
      setManualEntry(false);
      setManualQRData('');
    } catch (err) {
      // Error handling is done in handleQRCodeDetected
    }
  };

  const handleQRCodeDetected = async (qrData: string) => {
    // Prevent duplicate processing of the same QR code
    if (lastProcessedQR === qrData) {
      console.log('QR code already processed, ignoring:', qrData);
      return;
    }
    
    setScanning(false);
    setLastProcessedQR(qrData);
    
    try {
      // QR code should now be a simple pass_id string
      const passId = qrData.trim();
      console.log('Processing pass_id:', passId);
      
      // Validate customer and get loyalty info using pass_id
      await validateAndGetCustomerInfo(passId);
      
    } catch (err: any) {
      setError(err.message || "Failed to process QR code");
      toast.error("QR Code Error", {
        description: err.message || "Failed to process QR code"
      });
    }
  };

  const validateAndGetCustomerInfo = async (passId: string) => {
    if (!isAuthenticated || !scannerSession) {
      throw new Error("Scanner not authenticated");
    }

    console.log('Pass ID received:', passId);
    
    try {
      // Use the new unified validation endpoint with scanner credentials
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch('/functions/v1/validate-pass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ 
          pass_identifier: passId 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to validate pass');
      }

      const passData = await response.json();
      console.log('Pass validation result:', passData);

      // Check if it's a loyalty pass or coupon
      if (passData.type === 'coupon') {
        // Handle coupon pass
        const couponInfo: CouponInfo = {
          type: 'coupon',
          coupon_offer_id: passData.coupon_offer_id,
          customer_id: passData.customer_id,
          coupon_name: passData.coupon_name,
          coupon_header: passData.coupon_header,
          coupon_description: passData.coupon_description,
          discount_type: passData.discount_type,
          discount_value: passData.discount_value,
          minimum_purchase: passData.minimum_purchase,
          business_id: passData.business_id,
          business_name: passData.business_name,
          pass_id: passData.pass_id,
          pass_uuid: passData.pass_uuid, // Add the actual UUID for APNs
          google_wallet_object_id: passData.google_wallet_object_id,
          platform: passData.platform || 'google_wallet', // Default to Google if missing
          valid_until: passData.valid_until,
          can_redeem: passData.can_redeem,
          already_redeemed: passData.already_redeemed,
          redemption_date: passData.redemption_date,
        };

        // Verify business access for coupons
        if (couponInfo.business_id !== scannerSession.business_id) {
          throw new Error(`Access denied: This coupon belongs to "${couponInfo.business_name}" but you are logged in as "${scannerSession.business_name}".`);
        }

        setCustomerInfo(couponInfo);
        setOriginalCustomerInfo({ ...couponInfo });
        setCurrentPassId(passId);
        setVisitCounted(false);
        setPendingPoints(0);
        setPendingStamps(0);
        
        toast.success(`Coupon Found: ${couponInfo.coupon_name}`);
      } else {
        // Handle loyalty pass (existing logic)
        const loyaltyInfo: CustomerLoyaltyInfo = {
          customer_id: passData.customer_id,
          customer_name: passData.customer_name,
          program_name: passData.program_name,
          program_type: passData.program_type as 'points' | 'stamps',
          current_points: passData.current_points,
          current_stamps: passData.current_stamps,
          total_visits: passData.total_visits,
          business_id: passData.business_id,
          loyalty_program_id: passData.loyalty_program_id,
        };

        // Verify business access for loyalty passes
        if (loyaltyInfo.business_id !== scannerSession.business_id) {
          throw new Error(`Access denied: This loyalty pass belongs to "${passData.business_name}" but you are logged in as "${scannerSession.business_name}".`);
        }

        setCustomerInfo(loyaltyInfo);
        setOriginalCustomerInfo({ ...loyaltyInfo });
        setCurrentPassId(passId);
        setVisitCounted(false);
        setPendingPoints(0);
        setPendingStamps(0);
        
        toast.success(`Customer Found: ${loyaltyInfo.program_name} - ${loyaltyInfo.customer_id}`);
      }

    } catch (error: any) {
      console.error('Error validating pass:', error);
      throw new Error(error.message || 'Failed to validate pass');
    }
  };

  const updateGoogleWalletPass = async (passId: string, newPoints: number, newStamps: number, programType: string) => {
    try {
      console.log('Updating Google Wallet pass:', passId);
      
      const response = await fetch('https://pass.taginn.app/google-wallet/update-pass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pass_id: passId,
          current_points: newPoints,
          current_stamps: newStamps,
          program_type: programType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update Google Wallet pass');
      }

      const result = await response.json();
      console.log('🎉 Google Wallet pass updated:', result);
      
      return result;
    } catch (error: any) {
      console.error('Google Wallet update error:', error);
      throw error;
    }
  };

  const handleUpdatePass = async (action: 'add_points' | 'add_stamps' | 'redeem_reward' | 'redeem_coupon', value?: number) => {
    if (!customerInfo || !isAuthenticated || !scannerSession) {
      return;
    }

    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Updating pass:', { action, value });
      
      // DON'T create database transaction yet - just update pending amounts
      let addedPoints = 0;
      let addedStamps = 0;
      
      // Handle coupon redemption immediately (no undo functionality)
      if (action === 'redeem_coupon') {
        if (!('type' in customerInfo) || customerInfo.type !== 'coupon') {
          throw new Error('Invalid action: redeem_coupon can only be used with coupon passes');
        }

        // Call the coupon redemption API
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error("No active session");
        }

        const response = await fetch('/functions/v1/redeem-coupon', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            coupon_offer_id: customerInfo.coupon_offer_id,
            customer_id: customerInfo.customer_id,
            google_wallet_object_id: customerInfo.google_wallet_object_id,
            platform: customerInfo.platform, // Pass platform info for future Apple Wallet updates
            redemption_method: 'scanner'
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to redeem coupon');
        }

        const redemptionResult = await response.json();

        // Send Apple Push Notifications for coupon redemption (only for Apple Wallet coupons)
        if (customerInfo.platform === 'apple_wallet') {
          if (!customerInfo.pass_uuid) {
            toast.error('Update Error', { description: 'Pass UUID missing for Apple Wallet update' });
          } else {
            try {
              const apnResponse = await fetch('https://pass.taginn.app/apple-wallet/send-pass-update-notification', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  passId: customerInfo.pass_uuid // Use actual pass UUID for APNs calls
                }),
              });

              if (apnResponse.ok) {
                const apnResult = await apnResponse.json();
              }
            } catch (appleError) {
              // Don't show error to user as this is a background operation
            }
          }
        }

        // Show success message briefly, then start scanner
        toast.success("Coupon Redeemed!", {
          description: `${customerInfo.coupon_name} has been marked as used`
        });

        // Clear customer info and start scanner after brief delay
        setTimeout(() => {
          setCustomerInfo(null);
          setOriginalCustomerInfo(null);
          setCurrentPassId(null);
          setSuccess(null);
          setError(null);
          setLastProcessedQR(null);
          startScanning();
        }, 1500); // 1.5 second delay to show success message

        return; // Exit early for coupon redemption
      }

      // Calculate what to add based on action (loyalty passes only)
      if (action === 'add_points' && value) {
        addedPoints = value;
        setPendingPoints(prev => prev + value);
      } else if (action === 'add_stamps' && value) {
        addedStamps = value;
        setPendingStamps(prev => prev + value);
      } else if (action === 'redeem_reward') {
        // Redeem rewards need to be processed immediately
        // TODO: Handle redemption separately
        return;
      }
      
      // Update local customer info to show pending changes (loyalty passes only)
      if ('program_type' in customerInfo) {
        setCustomerInfo(prev => prev && 'program_type' in prev ? {
          ...prev,
          current_points: prev.current_points + addedPoints,
          current_stamps: prev.current_stamps + addedStamps,
          // Keep total_visits unchanged - will be updated after finalization
        } : prev);

        setSuccess(`Successfully ${action.replace('_', ' ')} for customer ${customerInfo.customer_name || customerInfo.customer_id}`);
        
        // Show immediate feedback with sound/vibration if possible
        if ('vibrate' in navigator) {
          navigator.vibrate(200); // Quick vibration feedback on mobile
        }
        
        // Show better feedback for consecutive additions
        const totalAddedPoints = pendingPoints + addedPoints;
        const totalAddedStamps = pendingStamps + addedStamps;
        const totalAdded = customerInfo.program_type === 'points' ? totalAddedPoints : totalAddedStamps;
        const addedText = `+${value} ${customerInfo.program_type} added! (Total pending: ${totalAdded})`;
        
        toast.success("✅ Success!", {
          description: `${addedText} - Press more buttons or wait for finalization.`
        });
      }
      
      
      // For consecutive additions, accumulate the total amount for undo
      if (lastAction && lastAction.action === action && undoCountdown && undoCountdown > 0) {
        // This is a consecutive addition - accumulate the amounts
        setLastAction({
          action,
          value: (lastAction.value || 0) + (value || 0),
          timestamp: Date.now()
        });
      } else {
        // This is a new action or first action
        setLastAction({
          action,
          value,
          timestamp: Date.now()
        });
      }
      
      // Always restart the 10-second countdown from the beginning
      startUndoCountdown();

    } catch (err: any) {
      setError(err.message || "Failed to update loyalty pass");
      toast.error("Update Failed", {
        description: err.message || "Failed to update loyalty pass"
      });
    } finally {
      setUpdating(false);
    }
  };

  // New clean undo countdown function
  const startUndoCountdown = () => {
    // Clear any existing timers first
    if (undoIntervalRef.current) {
      clearInterval(undoIntervalRef.current);
      undoIntervalRef.current = null;
    }
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    
    setUndoCountdown(10000); // 10 seconds
    
    undoIntervalRef.current = setInterval(() => {
      setUndoCountdown(prev => {
        if (prev === null || prev <= 100) {
          // Clear the interval
          if (undoIntervalRef.current) {
            clearInterval(undoIntervalRef.current);
            undoIntervalRef.current = null;
          }
          
          // Schedule the finalization with a timeout that we can cancel
          undoTimeoutRef.current = setTimeout(async () => {
            await finalizeTransaction();
            setCustomerInfo(null);
            setOriginalCustomerInfo(null);
            setCurrentPassId(null);
            setSuccess(null);
            setError(null);
            setLastProcessedQR(null);
            setLastAction(null);
            setVisitCounted(false);
            setPendingPoints(0);
            setPendingStamps(0);
            startScanning();
          }, 10000); // 10 seconds
          
          return null;
        }
        return prev - 100;
      });
    }, 100);
  };

  const handleUndo = async () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (!lastAction || !customerInfo || !originalCustomerInfo) {
      return;
    }
    
    // IMMEDIATELY stop ALL timers - this prevents any race conditions
    setUndoCountdown(null);
    if (undoIntervalRef.current) {
      clearInterval(undoIntervalRef.current);
      undoIntervalRef.current = null;
    }
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    
    setUpdating(true);
    
    try {
      
      // COMPLETE RESTORATION - as if the customer was just scanned fresh
      setCustomerInfo({ ...originalCustomerInfo });
      setPendingPoints(0);
      setPendingStamps(0);
      setLastAction(null);
      setVisitCounted(false);
      
      // Show success message
      setSuccess(`✅ Fully restored! Back to original state: ${originalCustomerInfo.current_points} points, ${originalCustomerInfo.current_stamps} stamps.`);
      
      toast.success("↩️ Complete Restoration! All changes undone. Back to original state.");
      
      // Clear success message after delay using ref
      successTimeoutRef.current = setTimeout(() => {
        setSuccess(null);
        successTimeoutRef.current = null;
      }, 5000); // Increased to 5 seconds to be more visible
      
    } catch (error: any) {
      setError(`Failed to undo: ${error.message}`);
      toast.error("❌ Undo Failed", {
        description: error.message || "Could not restore original state"
      });
    } finally {
      setUpdating(false);
    }
  };

  const finalizeTransaction = async () => {
    // Only create database transaction if there are pending changes
    if ((pendingPoints > 0 || pendingStamps > 0) && customerInfo && !visitCounted) {
      try {
        const programId = customerInfo.loyalty_program_id;
        if (!programId) {
          throw new Error('Loyalty program ID not found');
        }
        
        // Call pass-generator-server to handle both database and pass updates
        const response = await fetch('https://pass.taginn.app/loyalty/transaction', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: customerInfo.customer_id,
            program_id: programId,
            transaction_type: 'earn',
            purchase_amount: null,
            points_to_redeem: null,
            stamps_to_earn: pendingStamps > 0 ? pendingStamps : null,
            // For direct points addition (not purchase-based)
            direct_points: pendingPoints > 0 ? pendingPoints : null
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update loyalty pass');
        }

        const updatedData = await response.json();
        
        // Update local customer info with new balances
        setCustomerInfo(prev => prev && prev.type !== 'coupon' ? {
          ...prev,
          current_points: (prev.current_points || 0) + pendingPoints,
          current_stamps: (prev.current_stamps || 0) + pendingStamps,
          total_visits: prev.total_visits + 1
        } : prev);
        
        setVisitCounted(true);
      } catch (error) {
        // Transaction finalization failed
      }
    }
    
    // Reset pending amounts
    setPendingPoints(0);
    setPendingStamps(0);
    
    // Note: Google Wallet pass updates are now handled by the /loyalty/transaction endpoint
    // No need to call updateGoogleWalletPass separately - it's already been done

    // Send Apple Push Notifications for pass updates (only for Apple passes)
    if (currentPassId && customerInfo && customerInfo.wallet_type === 'apple') {
      try {
        const response = await fetch('https://pass.taginn.app/apple-wallet/send-pass-update-notification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            passId: currentPassId
          }),
        });

        if (response.ok) {
          const result = await response.json();
        }
      } catch (appleError) {
        // Don't show error to user as this is a background operation
      }
    }
  };

  const handleNextCustomer = async () => {
    // Stop any countdown and finalize the transaction
    setUndoCountdown(null);
    setLastAction(null);
    if (undoIntervalRef.current) {
      clearInterval(undoIntervalRef.current);
      undoIntervalRef.current = null;
    }
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    await finalizeTransaction();
    
    // Clear everything and start scanning
    setCustomerInfo(null);
    setOriginalCustomerInfo(null);
    setCurrentPassId(null);
    setSuccess(null);
    setError(null);
    setLastProcessedQR(null);
    setVisitCounted(false);
    setPendingPoints(0);
    setPendingStamps(0);
    startScanning();
  };

  // Show loading while checking scanner authentication
  if (scannerLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <Loader2 className="mx-auto h-12 w-12 text-blue-500 mb-4 animate-spin" />
              <h2 className="text-xl font-semibold mb-2">Checking Authentication</h2>
              <p className="text-gray-600">Please wait while we verify your scanner access...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show scanner login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-lg mx-auto">
          <ScannerLoginForm
            title="Scanner Login Required"
            description="Enter your scanner credentials to access the pass update system"
            onSuccess={() => {
              // Authentication successful, refresh page to load scanner interface
              toast.success(`Welcome! Scanner access granted for ${programName || 'the loyalty program'}`);
              // Refresh page to automatically load scanner interface
              setTimeout(() => {
                window.location.reload();
              }, 1000); // Small delay to show the success message
            }}
          />
          
          {/* Info section */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-800 mb-1">Scanner Access Information</p>
                <ul className="text-blue-700 space-y-1 text-xs">
                  <li>• Use scanner credentials provided by your business owner</li>
                  <li>• Scanner access works for all loyalty programs in your business</li>
                  <li>• Contact your manager if you need access</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return (
    <div className={`container mx-auto px-4 py-8 ${isMobile ? 'py-4' : 'py-8'}`}>
      <div className={`${isMobile ? 'max-w-full' : scanning ? 'max-w-5xl' : 'max-w-2xl'} mx-auto`}>
        {/* Show QR Scanner Card when scanning */}
        {scanning && (
          <QRCodeScanner
            isActive={scanning}
            onScan={handleQRCodeDetected}
            onError={(error) => {
              setError(error);
              setScanning(false);
            }}
            onClose={stopScanning}
          />
        )}

        {/* Floating notification alerts - positioned above the card */}
        {customerInfo && (error || success) && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl px-4">
            {error && (
              <Alert variant="destructive" className="mb-4 shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-300">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="border-green-200 bg-green-50 shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-300 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  <div className="flex items-center justify-between">
                    <span>{success}</span>
                    <div className="flex gap-2">
                      {undoCountdown && lastAction && (
                        <Button
                          onClick={handleUndo}
                          variant="outline"
                          size="sm"
                          className="bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200"
                          disabled={updating}
                        >
                          ↩️ Undo ({Math.ceil(undoCountdown / 1000)}s)
                        </Button>
                      )}
                      <Button
                        onClick={handleNextCustomer}
                        variant="default"
                        size="sm"
                        className="bg-primary hover:bg-primary/90 text-white"
                        disabled={updating}
                      >
                        Scan Another Customer →
                      </Button>
                    </div>
                  </div>
                  {undoCountdown && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span>Auto-scan in: {Math.ceil(undoCountdown / 1000)}s</span>
                        <div className="flex-1 bg-green-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-100" 
                            style={{ width: `${(undoCountdown / 10000) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        <p>Press "Scan Another Customer" to skip waiting, or "Undo" to reverse the action</p>
                        {(pendingPoints > 0 || pendingStamps > 0) && (
                          <p className="font-semibold mt-1 text-green-700">
                            Pending to add: {pendingPoints > 0 && `${pendingPoints} points`}
                            {pendingPoints > 0 && pendingStamps > 0 && ', '}
                            {pendingStamps > 0 && `${pendingStamps} stamps`}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {!undoCountdown && success.includes('undone') && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600">
                        Action reversed successfully. You can make a new action or scan another customer.
                      </p>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Show Customer Update Form when customer is found */}
        {customerInfo && (
          <Card className="mt-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-6 w-6" />
                  Update Customer Pass {isMobile ? '(Mobile)' : ''}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-sm font-medium">{programName}</p>
                    <p className="text-xs text-gray-500">Scanner: {username}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      scannerLogout();
                      toast.success("You have been logged out of scanner mode");
                    }}
                  >
                    <LogIn className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <PassUpdateForm
                customerInfo={customerInfo}
                onUpdate={handleUpdatePass}
                onCancel={async () => {
                  // Finalize any pending transactions first
                  await finalizeTransaction();
                  
                  setCustomerInfo(null);
                  setOriginalCustomerInfo(null);
                  setError(null);
                  setSuccess(null);
                  setLastProcessedQR(null); // Reset so they can scan again
                  setVisitCounted(false);
                  setPendingPoints(0);
                  setPendingStamps(0);
                  // Auto-start scanning for next customer
                  setTimeout(() => {
                    startScanning();
                  }, 100);
                }}
                isUpdating={updating}
              />
            </CardContent>
          </Card>
        )}

        {/* Show main interface when not scanning and no customer found */}
        {!scanning && !customerInfo && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-6 w-6" />
                  Update Customer Pass {isMobile ? '(Mobile)' : ''}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-sm font-medium">{programName}</p>
                    <p className="text-xs text-gray-500">Scanner: {username}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      scannerLogout();
                      toast.success("You have been logged out of scanner mode");
                    }}
                  >
                    <LogIn className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">{success}</AlertDescription>
                </Alert>
              )}

              <div className="text-center space-y-4">
                {!manualEntry && (
                  <div className="space-y-4">
                    <p className="text-gray-600 mb-4">
                      Scan a customer's loyalty pass QR code to update their points or stamps
                    </p>
                    
                    {/* Security Notice */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-blue-700">
                        <ShieldCheck className="h-4 w-4" />
                        <span className="text-sm font-medium">Security Notice</span>
                      </div>
                      <p className="text-sm text-blue-600 mt-1">
                        Camera access requires HTTPS. If scanning fails, use manual entry below.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Button onClick={startScanning} size="lg" className="w-full">
                        <Camera className="h-5 w-5 mr-2" />
                        Start QR Scanner
                      </Button>
                      <Button onClick={startManualEntry} size="lg" variant="outline" className="w-full">
                        <Keyboard className="h-5 w-5 mr-2" />
                        Manual Entry
                      </Button>
                    </div>
                  </div>
                )}

                {manualEntry && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <CardTitle className="text-sm font-medium">Manual QR Code Entry</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="qr-data">Pass ID</Label>
                        <Input
                          id="qr-data"
                          placeholder="Enter the pass ID from the QR code..."
                          value={manualQRData}
                          onChange={(e) => setManualQRData(e.target.value)}
                        />
                        <p className="text-sm text-gray-500">
                          Enter the pass ID from the QR code (e.g., "CUSTOMER123_1234567890").
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={handleManualSubmit}
                          disabled={!manualQRData.trim()}
                          className="flex-1"
                        >
                          Process Pass ID
                        </Button>
                        <Button
                          onClick={() => {
                            setManualEntry(false);
                            setManualQRData('');
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UpdatePassPage;